import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../App";
import styles from "./Provas.module.css";

// Types remain the same
interface Team {
    id: number;
    name: string;
    completedProvas?: {
        segueLinha: boolean;
        velocidade: boolean;
    };
}

interface ProvaStatusType {
    id?: number;
    teamId?: number;
    enabled?: boolean;
    provaType?: "segueLinha" | "velocidade" | "undefined";
    started?: boolean;
    startedTimeStamp?: string;
    ended?: boolean;
    endedTimeStamp?: string;
}

interface Penalty {
    id?: number;
    provaId?: number;
    nome: string;
    value: number;
}

interface ProvaHistory {
    id: number;
    teamId: number;
    teamName: string;
    provaType: string;
    startTime: number;
    endTime: number;
    penalties: Penalty[];
    disqualified: boolean;
}

type ViewState = "selection" | "team-selection" | "in-progress" | "completed";

const formatTimestamp = (timestamp: string): string => {
    const date = new Date(parseInt(timestamp));

    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");
    const ms = date.getMilliseconds().toString().padStart(3, "0");

    return `${hours}:${minutes}:${seconds}.${ms}`;
};

const Provas: React.FC = () => {
    const { isDarkMode, toggleTheme } = useContext(ThemeContext);
    const [viewState, setViewState] = useState<ViewState>("selection");
    const [selectedType, setSelectedType] = useState<
        "segueLinha" | "velocidade" | "undefined"
    >("undefined");
    const [teams, setTeams] = useState<Team[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [provaStatus, setProvaStatus] = useState<ProvaStatusType | null>(
        null
    );
    const [penalties, setPenalties] = useState<Penalty[]>([]);
    const [statusCheckInterval, setStatusCheckInterval] = useState<
        number | null
    >(null);
    const [error, setError] = useState<string | null>(null);
    const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
    const [canStopProva] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [fadeIn, setFadeIn] = useState<boolean>(false);
    const [customTimeValue, setCustomTimeValue] = useState<number>(60);

    // Predefined penalties with improved icons
    const predefinedPenalties = [
        { nome: "Falha de Zona", value: 30, icon: "🚧", color: "#FF9800" },
        { nome: "sair da linha", value: 2, icon: "🚦", color: "#FF5722" },
        { nome: "Tocar Obstáculos", value: 1, icon: "🛑", color: "#F44336" },
        { nome: "Arastar Obstáculo", value: 30, icon: "🛠️", color: "#FFEB3B" },
    ];

    // Animation effect on view changes
    useEffect(() => {
        setFadeIn(false);
        const timer = setTimeout(() => setFadeIn(true), 50);
        return () => clearTimeout(timer);
    }, [viewState]);

    // Calculate final time with penalties
    const calculateFinalTime = (
        baseTime: number,
        penalties: Penalty[]
    ): number => {
        const penaltyTime = penalties.reduce(
            (total, penalty) => total + penalty.value,
            0
        );
        return baseTime + penaltyTime;
    };

    // Add a predefined penalty
    const addPredefinedPenalty = (
        penaltyName: string,
        penaltyValue: number
    ) => {
        setPenalties([
            ...penalties,
            { nome: penaltyName, value: penaltyValue },
        ]);
    };

    // Stop prova manually
    const SetCustomTime = async (customTime: number) => {
        try {
            setIsLoading(true);
            await axios.post("/provas/setCustomTime", {
                provaId: provaStatus?.id,
                customTimeSeconds: customTime,
            });
            await checkProvaStatus();
        } catch (err) {
            setError("Falha ao parar a prova");
            console.error("Error stopping prova:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Load teams on component mount
    useEffect(() => {
        fetchTeams();
    }, []);

    // Clean up interval on unmount
    useEffect(() => {
        return () => {
            if (statusCheckInterval) {
                clearInterval(statusCheckInterval);
            }
        };
    }, [statusCheckInterval]);

    // Check the status of the current prova
    const checkProvaStatus = React.useCallback(async () => {
        try {
            const response = await axios.get("/provas/status");
            setProvaStatus(response.data);

            // If prova has ended, switch to completed state
            if (response.data.ended) {
                if (statusCheckInterval) {
                    clearInterval(statusCheckInterval);
                    setStatusCheckInterval(null);
                }
                setViewState("completed");
            }
        } catch (err) {
            setError("Falha ao verificar status da prova");
            console.error("Error checking prova status:", err);
        }
    }, [statusCheckInterval]);

    // Status polling when a prova is in progress
    useEffect(() => {
        if (viewState === "in-progress" && !statusCheckInterval) {
            checkProvaStatus();
            const interval = window.setInterval(checkProvaStatus, 100);
            setStatusCheckInterval(interval);
        } else if (viewState !== "in-progress" && statusCheckInterval) {
            clearInterval(statusCheckInterval);
            setStatusCheckInterval(null);
        }
    }, [viewState, statusCheckInterval, checkProvaStatus]);

    // Track time since prova started
    useEffect(() => {
        let timer: number | null = null;

        if (
            viewState === "in-progress" &&
            provaStatus?.started &&
            provaStatus?.startedTimeStamp
        ) {
            const initialElapsed = Math.floor(
                (Date.now() - (parseInt(provaStatus.startedTimeStamp) ?? 0)) /
                    1000
            );
            setElapsedSeconds(initialElapsed);

            timer = window.setInterval(() => {
                const elapsed = Math.floor(
                    (Date.now() -
                        parseInt(provaStatus.startedTimeStamp || "0")) /
                        1000
                );
                setElapsedSeconds(elapsed);
            }, 1000);
        }

        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [
        viewState,
        provaStatus?.started,
        provaStatus?.startedTimeStamp,
        canStopProva,
    ]);

    // Fetch teams from the API with completion status
    const fetchTeams = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get("/team/");
            const teamsData = response.data;

            const historyResponse = await axios.get("/provas/history");
            const history = historyResponse.data;

            // Mark teams that have completed provas
            const teamsWithStatus = teamsData.map((team: Team) => {
                const completedProvas = {
                    segueLinha: false,
                    velocidade: false,
                };

                history.forEach((prova: ProvaHistory) => {
                    if (prova.teamId === team.id && !prova.disqualified) {
                        if (prova.provaType === "segueLinha") {
                            completedProvas.segueLinha = true;
                        } else if (prova.provaType === "velocidade") {
                            completedProvas.velocidade = true;
                        }
                    }
                });

                return {
                    ...team,
                    completedProvas,
                };
            });

            setTeams(teamsWithStatus);
        } catch (err) {
            setError("Falha ao carregar equipas");
            console.error("Error fetching teams:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Start a prova with the selected team and type
    const startProva = async () => {
        if (!selectedTeam) {
            setError("Nenhuma equipa selecionada");
            return;
        }

        try {
            setIsLoading(true);
            await axios.post("/provas/setEnabled", {});
            await axios.post("/provas/setProvaType", {
                provaType: selectedType,
            });
            await axios.post("/provas/setTeam", { teamId: selectedTeam.id });
            setViewState("in-progress");
            await checkProvaStatus();
        } catch (err) {
            setError("Falha ao iniciar prova");
            console.error("Error starting prova:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Remove a penalty
    const removePenalty = (index: number) => {
        const updatedPenalties = [...penalties];
        updatedPenalties.splice(index, 1);
        setPenalties(updatedPenalties);
    };

    // Finish prova
    const finishProva = async () => {
        try {
            setIsLoading(true);
            const currentProvaId = provaStatus?.id; // Get ID from current status

            // Send penalties BEFORE calling /provas/finish
            if (penalties.length > 0 && currentProvaId) {
                await Promise.all(
                    penalties.map((penalty) =>
                        axios.post("/provas/penalty", {
                            provaId: currentProvaId, // Use the ID of the currently active/ending prova
                            nome: penalty.nome,
                            value: penalty.value,
                        })
                    )
                );
            }

            // Now, call the finish endpoint
            await axios.post("/provas/finish", {});

            setSelectedTeam(null);
            setPenalties([]);
            await fetchTeams();
            setViewState("team-selection");
        } catch (err) {
            setError("Falha ao finalizar prova");
            console.error("Error finishing prova:", err);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle type selection
    const handleTypeSelection = (type: "segueLinha" | "velocidade") => {
        setSelectedType(type);
        setViewState("team-selection");
    };

    // Handle team selection
    const handleTeamSelect = (team: Team) => {
        setSelectedTeam(team);
    };

    // Render Selection View
    const renderSelectionView = () => (
        <div
            className={`${styles.card} ${styles.selectionView} ${
                fadeIn ? styles.fadeIn : ""
            }`}>
            <div className={styles.cardHeader}>
                <h2>Selecionar Tipo de Prova</h2>
            </div>

            <div className={styles.cardBody}>
                <div className={styles.buttonContainer}>
                    <button
                        className={`${styles.provaCard} ${styles.velocidadeCard}`}
                        onClick={() => handleTypeSelection("velocidade")}>
                        <div className={styles.cardIcon}>🏎️</div>
                        <div className={styles.cardTitle}>Velocidade</div>
                        <div className={styles.cardDescription}>
                            Teste de velocidade máxima
                        </div>
                        <div className={styles.cardBadge}>Velocidade</div>
                    </button>

                    <button
                        className={`${styles.provaCard} ${styles.segueLinhaCard}`}
                        onClick={() => handleTypeSelection("segueLinha")}>
                        <div className={styles.cardIcon}>🛣️</div>
                        <div className={styles.cardTitle}>Segue Linha</div>
                        <div className={styles.cardDescription}>
                            Teste de precisão no percurso
                        </div>
                        <div className={styles.cardBadge}>Precisão</div>
                    </button>
                </div>
            </div>

            <div className={styles.cardFooter}>
                <button onClick={toggleTheme} className={styles.themeToggle}>
                    {isDarkMode ? "☀️ Modo claro" : "🌙 Modo escuro"}
                </button>
            </div>
        </div>
    );

    // Render Team Selection View
    const renderTeamSelectionView = () => (
        <div
            className={`${styles.card} ${styles.teamSelectionView} ${
                fadeIn ? styles.fadeIn : ""
            }`}>
            <div className={styles.cardHeader}>
                <div className={styles.headerWithAction}>
                    <div>
                        <h2>Selecionar Equipa</h2>
                        <div className={styles.provaTypeIndicator}>
                            {selectedType === "velocidade" ? (
                                <span
                                    className={`${styles.badge} ${styles.velocidadeBadge}`}>
                                    <span className={styles.badgeIcon}>🏎️</span>{" "}
                                    Velocidade
                                </span>
                            ) : (
                                <span
                                    className={`${styles.badge} ${styles.segueLinhaeBadge}`}>
                                    <span className={styles.badgeIcon}>🛣️</span>{" "}
                                    Segue Linha
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        className={styles.refreshButton}
                        onClick={fetchTeams}
                        disabled={isLoading}>
                        <span
                            className={`${styles.buttonIcon} ${
                                isLoading ? styles.spinning : ""
                            }`}>
                            🔄
                        </span>
                        {isLoading ? "A carregar..." : "Atualizar"}
                    </button>
                </div>
            </div>

            <div className={styles.cardBody}>
                <div className={styles.teamListContainer}>
                    <h3>Equipas Disponíveis</h3>
                    {teams.length > 0 ? (
                        <ul className={styles.teamList}>
                            {[...teams]
                                .sort((a, b) => {
                                    const aCompleted =
                                        (selectedType !== "undefined" &&
                                            a.completedProvas?.[
                                                selectedType
                                            ]) ||
                                        false;
                                    const bCompleted =
                                        (selectedType !== "undefined" &&
                                            b.completedProvas?.[
                                                selectedType
                                            ]) ||
                                        false;

                                    if (aCompleted === bCompleted) return 0;
                                    return aCompleted ? 1 : -1;
                                })
                                .map((team, index) => {
                                    const hasCompleted =
                                        (selectedType !== "undefined" &&
                                            team.completedProvas?.[
                                                selectedType
                                            ]) ||
                                        false;

                                    return (
                                        <li
                                            key={team.id}
                                            className={`${styles.teamCard} 
                                                ${
                                                    selectedTeam?.id === team.id
                                                        ? styles.selected
                                                        : ""
                                                }
                                                ${
                                                    hasCompleted
                                                        ? styles.completed
                                                        : ""
                                                }`}
                                            onClick={() =>
                                                handleTeamSelect(team)
                                            }
                                            title={
                                                hasCompleted
                                                    ? `Já completou a prova de ${
                                                          selectedType ===
                                                          "velocidade"
                                                              ? "Velocidade"
                                                              : "Segue Linha"
                                                      }`
                                                    : ""
                                            }
                                            style={{
                                                animationDelay: `${
                                                    index * 50
                                                }ms`,
                                            }}>
                                            <div
                                                className={
                                                    styles.teamCardContent
                                                }>
                                                <span
                                                    className={styles.teamName}>
                                                    {team.name}
                                                </span>
                                                {hasCompleted && (
                                                    <span
                                                        className={
                                                            styles.completedBadge
                                                        }>
                                                        <span
                                                            className={
                                                                styles.checkmark
                                                            }>
                                                            ✓
                                                        </span>
                                                        <span
                                                            className={
                                                                styles.completedText
                                                            }>
                                                            Concluído
                                                        </span>
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                        </ul>
                    ) : (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyStateIcon}>📋</div>
                            <p className={styles.emptyStateMessage}>
                                Não existem equipas disponíveis
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.cardFooter}>
                <button
                    className={styles.secondaryButton}
                    onClick={() => setViewState("selection")}>
                    <span className={styles.buttonIcon}>⬅️</span>
                    Voltar
                </button>
                <button
                    className={`${styles.primaryButton} ${
                        !selectedTeam ? styles.disabled : ""
                    }`}
                    disabled={!selectedTeam || isLoading}
                    onClick={startProva}>
                    {isLoading ? (
                        <>
                            <span
                                className={`${styles.buttonIcon} ${styles.spinning}`}>
                                🔄
                            </span>
                            A iniciar...
                        </>
                    ) : (
                        <>
                            <span className={styles.buttonIcon}>▶️</span>
                            Iniciar Prova
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    // Render In Progress View
    const renderInProgressView = () => (
        <div
            className={`${styles.card} ${styles.inProgressView} ${
                fadeIn ? styles.fadeIn : ""
            }`}>
            <div className={styles.cardHeader}>
                <h2>Prova em Progresso</h2>
                <span className={`${styles.badge} ${styles.activeBadge}`}>
                    Ativo
                </span>
            </div>

            <div className={styles.cardBody}>
                <div className={styles.statusCard}>
                    <div className={styles.statusGrid}>
                        <div className={styles.statusItem}>
                            <div className={styles.statusLabel}>Tipo</div>
                            <div className={styles.statusValue}>
                                {selectedType === "velocidade" ? (
                                    <span
                                        className={`${styles.badge} ${styles.velocidadeBadge}`}>
                                        <span className={styles.badgeIcon}>
                                            🏎️
                                        </span>{" "}
                                        Velocidade
                                    </span>
                                ) : (
                                    <span
                                        className={`${styles.badge} ${styles.segueLinhaeBadge}`}>
                                        <span className={styles.badgeIcon}>
                                            🛣️
                                        </span>{" "}
                                        Segue Linha
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className={styles.statusItem}>
                            <div className={styles.statusLabel}>Equipa</div>
                            <div className={styles.statusValue}>
                                <span className={styles.teamStatusValue}>
                                    {selectedTeam?.name}
                                </span>
                            </div>
                        </div>

                        <div className={styles.statusItem}>
                            <div className={styles.statusLabel}>Estado</div>
                            <div className={styles.statusValue}>
                                {provaStatus?.started ? (
                                    <span
                                        className={`${styles.statusChip} ${styles.statusStarted}`}>
                                        <span className={styles.statusIcon}>
                                            ✅
                                        </span>{" "}
                                        Iniciada
                                    </span>
                                ) : (
                                    <span
                                        className={`${styles.statusChip} ${styles.statusPending}`}>
                                        <span className={styles.statusIcon}>
                                            ⏳
                                        </span>{" "}
                                        A aguardar início
                                    </span>
                                )}
                            </div>
                        </div>

                        {provaStatus?.startedTimeStamp && (
                            <div className={styles.statusItem}>
                                <div className={styles.statusLabel}>Início</div>
                                <div
                                    className={`${styles.statusValue} ${styles.timestampValue}`}>
                                    {formatTimestamp(
                                        provaStatus.startedTimeStamp
                                    )}
                                </div>
                            </div>
                        )}

                        {/* {provaStatus?.started && (
                            <div className={styles.statusItem}>
                                <div className={styles.statusLabel}>
                                    Tempo decorrido
                                </div>
                                <div
                                    className={`${styles.statusValue} ${styles.elapsedTimeValue}`}>

                                </div>
                            </div>
                        )} */}
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionTitle}>
                        <h3>Adicionar penalizações</h3>
                    </div>

                    <div className={styles.penaltyGrid}>
                        {predefinedPenalties.map((penalty, index) => (
                            <button
                                key={index}
                                className={styles.penaltyCard}
                                onClick={() =>
                                    addPredefinedPenalty(
                                        penalty.nome,
                                        penalty.value
                                    )
                                }
                                style={
                                    {
                                        "--penalty-color": penalty.color,
                                        animationDelay: `${index * 100}ms`,
                                    } as React.CSSProperties
                                }>
                                <div className={styles.penaltyCardIcon}>
                                    {penalty.icon}
                                </div>
                                <div className={styles.penaltyCardContent}>
                                    <div className={styles.penaltyCardName}>
                                        {penalty.nome}
                                    </div>
                                    <div className={styles.penaltyCardValue}>
                                        +{penalty.value}s
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {penalties.length > 0 && (
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionTitle}>
                            <h3>Penalizações Registradas</h3>
                            <span className={styles.penaltyCount}>
                                {penalties.length} penalização(ões)
                            </span>
                        </div>

                        <ul className={styles.penaltyList}>
                            {penalties.map((penalty, index) => (
                                <li key={index} className={styles.penaltyItem}>
                                    <div className={styles.penaltyItemContent}>
                                        <span className={styles.penaltyName}>
                                            {penalty.nome}
                                        </span>
                                        <span className={styles.penaltyValue}>
                                            +{penalty.value}s
                                        </span>
                                    </div>
                                    <button
                                        className={styles.penaltyRemoveButton}
                                        onClick={() => removePenalty(index)}>
                                        <span className={styles.removeIcon}>
                                            ✕
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className={styles.stopProvaSection}>
                    <button
                        className={`${styles.stopProvaButton} ${
                            !canStopProva ? styles.disabled : ""
                        }`}
                        onClick={() => SetCustomTime(180)}
                        disabled={!canStopProva || isLoading}>
                        <span className={styles.buttonDangerIcon}>⚠️</span>
                        {isLoading
                            ? "A parar..."
                            : "Parar Prova (Tempo Máximo: 180s & 3 Minutos)"}
                    </button>
                    <div className={styles.stopProvaNote}>
                        {canStopProva ? (
                            <span>
                                Termina a prova com o tempo máximo de 180
                                segundos
                            </span>
                        ) : (
                            <span>
                                Pode parar a prova após{" "}
                                {Math.max(0, 180 - elapsedSeconds)} segundos
                            </span>
                        )}
                    </div>
                </div>
                <div className={styles.stopProvaSection}>
                    <button
                        className={`${styles.stopProvaButton} ${
                            !canStopProva ? styles.disabled : ""
                        }`}
                        onClick={() => SetCustomTime(30)}
                        disabled={!canStopProva || isLoading}>
                        <span className={styles.buttonDangerIcon}>⚠️</span>
                        {isLoading ? "..." : "Prova Falhada ( 30 segundos )"}
                    </button>
                    <div className={styles.stopProvaNote}>
                        <span>Termina a prova com o tempo de 30 segundos</span>
                    </div>
                </div>
                <div className={styles.stopProvaSection}>
                    <div className={styles.customTimeInputGroup}>
                        <input
                            className={styles.customTimeInput}
                            value={customTimeValue}
                            onChange={(e) =>
                                setCustomTimeValue(Number(e.target.value))
                            }
                            min="1"
                            max="600"
                            placeholder="Tempo em segundos"
                        />
                        <button
                            className={`${styles.customTimeButton} ${
                                !canStopProva ? styles.disabled : ""
                            }`}
                            onClick={() => SetCustomTime(customTimeValue)}
                            disabled={!canStopProva || isLoading}>
                            <span className={styles.buttonDangerIcon}>⏱️</span>
                            {isLoading
                                ? "A parar..."
                                : "Parar com Tempo Personalizado"}
                        </button>
                    </div>
                    <div className={styles.stopProvaNote}>
                        <span>Termina a prova com um tempo personalizado</span>
                    </div>
                </div>

                <div className={styles.waitingCard}>
                    <div className={styles.pulsingDot}></div>
                    <span>
                        Aguardando sinal do Arduino para concluir a prova...
                    </span>
                </div>
            </div>
        </div>
    );

    // Render Completed View
    const renderCompletedView = () => (
        <div
            className={`${styles.card} ${styles.completedView} ${
                fadeIn ? styles.fadeIn : ""
            }`}>
            <div className={styles.cardHeader}>
                <h2>Prova Concluída</h2>
                <span className={`${styles.badge} ${styles.completedBadge}`}>
                    <span className={styles.badgeIcon}>🏁</span>
                    Finalizada
                </span>
            </div>

            <div className={styles.cardBody}>
                <div className={styles.statusCard}>
                    <div className={styles.statusGrid}>
                        <div className={styles.statusItem}>
                            <div className={styles.statusLabel}>Tipo</div>
                            <div className={styles.statusValue}>
                                {selectedType === "velocidade" ? (
                                    <span
                                        className={`${styles.badge} ${styles.velocidadeBadge}`}>
                                        <span className={styles.badgeIcon}>
                                            🏎️
                                        </span>{" "}
                                        Velocidade
                                    </span>
                                ) : (
                                    <span
                                        className={`${styles.badge} ${styles.segueLinhaeBadge}`}>
                                        <span className={styles.badgeIcon}>
                                            🛣️
                                        </span>{" "}
                                        Segue Linha
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className={styles.statusItem}>
                            <div className={styles.statusLabel}>Equipa</div>
                            <div className={styles.statusValue}>
                                <span className={styles.teamStatusValue}>
                                    {selectedTeam?.name}
                                </span>
                            </div>
                        </div>

                        {provaStatus?.startedTimeStamp && (
                            <div className={styles.statusItem}>
                                <div className={styles.statusLabel}>Início</div>
                                <div
                                    className={`${styles.statusValue} ${styles.timestampValue}`}>
                                    {formatTimestamp(
                                        provaStatus.startedTimeStamp
                                    )}
                                </div>
                            </div>
                        )}

                        {provaStatus?.endedTimeStamp && (
                            <div className={styles.statusItem}>
                                <div className={styles.statusLabel}>Fim</div>
                                <div
                                    className={`${styles.statusValue} ${styles.timestampValue}`}>
                                    {formatTimestamp(
                                        provaStatus.endedTimeStamp
                                    )}
                                </div>
                            </div>
                        )}

                        {provaStatus?.startedTimeStamp &&
                            provaStatus?.endedTimeStamp && (
                                <div className={styles.statusItem}>
                                    <div className={styles.statusLabel}>
                                        Duração base
                                    </div>
                                    <div
                                        className={`${styles.statusValue} ${styles.durationValue}`}>
                                        {(
                                            (parseInt(
                                                provaStatus.endedTimeStamp
                                            ) -
                                                parseInt(
                                                    provaStatus.startedTimeStamp
                                                )) /
                                            1000
                                        ).toFixed(3)}{" "}
                                        segundos
                                    </div>
                                </div>
                            )}

                        {penalties.length > 0 &&
                            provaStatus?.startedTimeStamp &&
                            provaStatus?.endedTimeStamp && (
                                <div className={styles.statusItem}>
                                    <div className={styles.statusLabel}>
                                        Tempo com penalizações
                                    </div>
                                    <div
                                        className={`${styles.statusValue} ${styles.finalTimeValue}`}>
                                        {calculateFinalTime(
                                            (parseInt(
                                                provaStatus.endedTimeStamp
                                            ) -
                                                parseInt(
                                                    provaStatus.startedTimeStamp
                                                )) /
                                                1000,
                                            penalties
                                        ).toFixed(3)}{" "}
                                        segundos
                                    </div>
                                </div>
                            )}
                    </div>
                </div>

                <div className={styles.sectionCard}>
                    <div className={styles.sectionTitle}>
                        <h3>Adicionar penalizações</h3>
                    </div>

                    <div className={styles.penaltyGrid}>
                        {predefinedPenalties.map((penalty, index) => (
                            <button
                                key={index}
                                className={styles.penaltyCard}
                                onClick={() =>
                                    addPredefinedPenalty(
                                        penalty.nome,
                                        penalty.value
                                    )
                                }
                                style={
                                    {
                                        "--penalty-color": penalty.color,
                                        animationDelay: `${index * 100}ms`,
                                    } as React.CSSProperties
                                }>
                                <div className={styles.penaltyCardIcon}>
                                    {penalty.icon}
                                </div>
                                <div className={styles.penaltyCardContent}>
                                    <div className={styles.penaltyCardName}>
                                        {penalty.nome}
                                    </div>
                                    <div className={styles.penaltyCardValue}>
                                        +{penalty.value}s
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {penalties.length > 0 && (
                    <div className={styles.sectionCard}>
                        <div className={styles.sectionTitle}>
                            <h3>Penalizações Registradas</h3>
                            <span className={styles.penaltyCount}>
                                {penalties.length} penalização(ões)
                            </span>
                        </div>

                        <ul className={styles.penaltyList}>
                            {penalties.map((penalty, index) => (
                                <li key={index} className={styles.penaltyItem}>
                                    <div className={styles.penaltyItemContent}>
                                        <span className={styles.penaltyName}>
                                            {penalty.nome}
                                        </span>
                                        <span className={styles.penaltyValue}>
                                            +{penalty.value}s
                                        </span>
                                    </div>
                                    <button
                                        className={styles.penaltyRemoveButton}
                                        onClick={() => removePenalty(index)}>
                                        <span className={styles.removeIcon}>
                                            ✕
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>

                        <div className={styles.penaltySummary}>
                            <span className={styles.penaltySummaryLabel}>
                                Total de penalizações:
                            </span>
                            <span className={styles.penaltySummaryValue}>
                                +
                                {penalties.reduce(
                                    (sum, penalty) => sum + penalty.value,
                                    0
                                )}
                                s
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.cardFooter}>
                <button
                    className={`${styles.primaryButton} ${styles.finishButton}`}
                    onClick={finishProva}
                    disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <span
                                className={`${styles.buttonIcon} ${styles.spinning}`}>
                                🔄
                            </span>
                            A salvar...
                        </>
                    ) : (
                        <>
                            <span className={styles.buttonIcon}>✅</span>
                            Finalizar e Salvar
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    // Render the appropriate view based on the current state
    const renderView = () => {
        switch (viewState) {
            case "selection":
                return renderSelectionView();
            case "team-selection":
                return renderTeamSelectionView();
            case "in-progress":
                return renderInProgressView();
            case "completed":
                return renderCompletedView();
            default:
                return null;
        }
    };

    return (
        <div
            className={`${styles.provasContainer} ${
                isDarkMode ? styles.darkTheme : styles.lightTheme
            }`}>
            {error && (
                <div
                    className={styles.errorToast}
                    onClick={() => setError(null)}>
                    <span className={styles.errorIcon}>⚠️</span>
                    <span className={styles.errorMessage}>{error}</span>
                    <span className={styles.dismissIcon}>✕</span>
                </div>
            )}
            {renderView()}
        </div>
    );
};

export default Provas;
